import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

function findByNat(sub, nat) {
  return sub.find((s) => s.nat === nat);
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

// book hamesha visible: 0 pe book-black, minus red / plus green
function bookOf(exposures, item) {
  return roundBook(exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

// desktop nation-name book + range (reference: `mr-2 casino-book` 0 + info
// icon `collapsed` + `icon-range collapse` R:min-max)
function DesktopRange({ id, item, book, open, onToggle }) {
  return (
    <div className="float-right">
      <span className={`mr-2 casino-book ${bookCls(book)}`}>{fmtBook(book)}</span>
      {' '}
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={String(open)}
        className={`fas fa-info-circle${open ? '' : ' collapsed'}`}
        onClick={onToggle}
      ></i>
      {' '}
      <div id={id} className={`icon-range collapse${open ? ' show' : ''}`}>
        {'\n                          R:'}
        <span>{fmtAmt(item?.min)}</span>
        -
        <span>{fmtAmt(item?.max)}</span>
      </div>
    </div>
  );
}

// ─── Desktop layout (d-none-small) ───────────────────────────────────────────

const DesktopLayout = memo(function DesktopLayout({ sub, onBet, exposures }) {
  const playerA = findByNat(sub, 'Player A');
  const playerB = findByNat(sub, 'Player B');
  const [ranges, setRanges] = useState({});
  const toggle = (id) => setRanges((r) => ({ ...r, [id]: !r[id] }));

  const side = (item, cls, label, rangeId) => (
    <div className={cls}>
      <div className="casino-box-row">
        <div className="casino-nation-name no-border casino-bl-box-title">
          <div className={label === 'Player A' ? 'playera' : 'playerb'}>{label}</div>
        </div>
        {' '}
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item"><b>Back</b></div>
          {' '}
          <div className="casino-bl-box-item"><b>Lay</b></div>
        </div>
      </div>
      {' '}
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>Main</b>
          {' '}
          <DesktopRange
            id={rangeId}
            item={item}
            book={bookOf(exposures, item)}
            open={!!ranges[rangeId]}
            onToggle={() => toggle(rangeId)}
          />
        </div>
        {' '}
        <div className="casino-bl-box">
          <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}>
            <span className="casino-box-odd">{item?.b || 0}</span>
          </div>
          {' '}
          <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'lay', item.l, item)}>
            <span className="casino-box-odd">{item?.l || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="teen1daycasino-container d-none-small">
      {side(playerA, 'teen1dayleft', 'Player A', 'range1')}
      {' '}
      <div className="teen1daycenter"></div>
      {' '}
      {side(playerB, 'teen1dayright', 'Player B', 'range7')}
    </div>
  );
});

// ─── Mobile layout (d-none-big) ──────────────────────────────────────────────

const MobileLayout = memo(function MobileLayout({ sub, onBet, exposures }) {
  const playerA = findByNat(sub, 'Player A');
  const playerB = findByNat(sub, 'Player B');
  const players = [playerA, playerB].filter(Boolean);
  const [openRange, setOpenRange] = useState(false);
  const rangeItem = playerA || playerB;

  return (
    <div className="teen1daycasino-container d-none-big">
      <div className="casino-box-row">
        <div className="casino-bl-box casino-bl-box-title">
          <div className="casino-bl-box-item">
            <b>Main</b>
            {' '}
            <div className="float-right">
              <i
                data-toggle="collapse"
                data-target="#range1"
                className="fas fa-info-circle float-right"
                onClick={() => setOpenRange((o) => !o)}
              ></i>
              {' '}
              <div id="range1" className={`collapse icon-range${openRange ? ' show' : ''}`}>
                {'\n                          R:'}
                <span>{fmtAmt(rangeItem?.min)}</span>
                -
                <span>{fmtAmt(rangeItem?.max)}</span>
              </div>
            </div>
          </div>
          <div className="casino-bl-box-item"><b>Back</b></div>
          <div className="casino-bl-box-item"><b>Lay</b></div>
        </div>
        {players.map((item) => {
          const book = bookOf(exposures, item);
          return (
            <div key={item.sid} className="casino-bl-box">
              <div className="casino-bl-box-item casino-odds-name">
                <span>{item.nat}</span>
                {' '}
                <span className={`float-right ${bookCls(book)}`}>{fmtBook(book)}</span>
              </div>
              <div className={`back casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'back', item.b, item)}>
                <span className="casino-box-odd">{item.b || 0}</span>
              </div>
              <div className={`lay casino-bl-box-item${suspCls(item)}`} onClick={isSusp(item) ? undefined : () => onBet(item.sid, 'lay', item.l, item)}>
                <span className="casino-box-odd">{item.l || 0}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─── Main export ─────────────────────────────────────────────────────────────

const BetTableInstantTeenpatti3 = memo(function BetTableInstantTeenpatti3({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});

  return (
    <>
      <DesktopLayout sub={sub} onBet={handleBet} exposures={exposures} />
      <MobileLayout sub={sub} onBet={handleBet} exposures={exposures} />
    </>
  );
});

export default BetTableInstantTeenpatti3;
